import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  apiAnonymizeAccount,
  apiAnonymizeCoachRequest,
  apiApproveCoachRequest,
  apiChangeAdminPassword,
  apiCreateEditor,
  apiCreateGovMember,
  apiDeleteAccount,
  apiDeleteSurveyAccess,
  apiPurgeAccount,
  apiGetAdminDashboard,
  apiGetAuditLogs,
  apiGetTicket,
  apiListTickets,
  apiReplyToTicket,
  apiUpdateTicketStatus,
  apiGetCoachRequests,
  apiGetGuardianPending,
  apiRecordGuardianConsent,
  apiGetEditors,
  apiGetGovMembers,
  apiGetSurveyAccesses,
  apiGrantBlogAccess,
  apiIssueSurveyAccess,
  apiRejectCoachRequest,
  apiRevokeBlogAccess,
  apiRevokeSurveyAccess,
  apiResendVerificationAdmin,
  apiSearchAccount,
  apiSearchUnverifiedAccount,
  apiValidateAccount,
} from '../services/api';
import {
  TICKET_CATEGORY_LABELS,
  TICKET_STATUS_LABELS,
  formatTicketDate,
} from '../constants/support';
import './Admin.css';

const StatCard = ({ label, value, color = 'blue' }) => (
  <div className="admin-card">
    <div className={`admin-card__value admin-card__value--${color}`}>{value}</div>
    <div className="admin-card__label">{label}</div>
  </div>
);

const DistributionList = ({ title, buckets }) => (
  <div className="admin-distribution">
    <h3 className="admin-distribution__title">{title}</h3>
    {buckets && buckets.length > 0 ? (
      <ul className="admin-distribution__list">
        {buckets.map((b) => (
          <li key={b.label} className="admin-distribution__row">
            <span>{b.label}</span>
            <span className="admin-distribution__count">{b.count}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="admin-distribution__empty">Aucune donnée.</p>
    )}
  </div>
);

const UnverifiedAccountLookup = ({ accessToken }) => {
  const [email, setEmail] = useState('');
  const [account, setAccount] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSearch = async () => {
    setError('');
    setMessage('');
    setAccount(null);
    try {
      const result = await apiSearchUnverifiedAccount(email.trim(), accessToken);
      setAccount(result);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleValidate = async () => {
    try {
      await apiValidateAccount(account.id, accessToken);
      setMessage('Compte validé.');
      setAccount(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResend = async () => {
    try {
      await apiResendVerificationAdmin(account.id, accessToken);
      setMessage('Code renvoyé.');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <section className="admin-section">
      <h2 className="admin-section__title">Vérification de compte</h2>
      <p className="admin-page__notice--muted">
        Recherche ciblée par email — aucune liste de comptes n'est affichée, conformément au
        principe d'accès au strict nécessaire.
      </p>
      <div className="admin-lookup">
        <input
          type="email"
          className="admin-lookup__input"
          placeholder="email@exemple.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button className="admin-lookup__button" onClick={handleSearch}>
          Rechercher
        </button>
      </div>

      {error && <p className="admin-page__error">{error}</p>}
      {message && <p className="admin-page__notice--muted">{message}</p>}

      {account && (
        <div className="admin-card admin-lookup__result">
          <p>
            <strong>{account.prenom} {account.nom}</strong> — {account.mail}
          </p>
          <p className="admin-card__label">
            Inscrit(e) le{' '}
            {account.consent_at ? new Date(account.consent_at).toLocaleDateString('fr-FR') : '—'}
          </p>
          <div className="admin-lookup__actions">
            <button className="admin-lookup__button" onClick={handleValidate}>
              Valider
            </button>
            <button className="admin-lookup__button" onClick={handleResend}>
              Renvoyer un code
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

const ACCOUNT_TYPE_LABELS = { sportive: 'Sportive', coach: 'Coach', editor: 'Rédaction' };

const AccountErasureSection = ({ accessToken }) => {
  const [email, setEmail] = useState('');
  const [account, setAccount] = useState(null);
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setAccount(null);
    setPassword('');
    try {
      setAccount(await apiSearchAccount(email.trim(), accessToken));
    } catch (err) {
      setError(err.message);
    }
  };

  // Les trois gestes partagent tout sauf l'appel et le texte de confirmation :
  // même mot de passe, même remise à zéro du formulaire, même gestion d'erreur.
  const runErasure = async (confirmText, action) => {
    // L'email est rappelé en toutes lettres : c'est la dernière occasion de voir
    // qu'on s'apprête à effacer le mauvais compte, la ligne n'ayant plus rien
    // d'identifiable ensuite.
    if (!window.confirm(confirmText)) return;

    setError('');
    setMessage('');
    try {
      const res = await action();
      setMessage(res.message);
      setAccount(null);
      setEmail('');
      setPassword('');
    } catch (err) {
      setError(err.message);
    }
  };

  const suivisWarning = account && account.suivis_count > 0
    ? `${account.suivis_count} suivi(s) coach↔sportive seront supprimés. `
    : '';

  const handleAnonymize = () =>
    runErasure(
      `Anonymiser le compte de ${account.prenom} ${account.nom} (${account.mail}) ?\n\n`
      + suivisWarning
      + `Les données personnelles seront effacées et l'adresse redeviendra disponible. `
      + `Les relevés de santé sont CONSERVÉS (non rattachables). Pour une demande `
      + `de suppression, utilisez plutôt « Effacer toutes les données ». `
      + `Cette action est irréversible.`,
      () => apiAnonymizeAccount(account.id, password, accessToken),
    );

  const handlePurge = () =>
    runErasure(
      `Effacer TOUTES les données de ${account.prenom} ${account.nom} `
      + `(${account.mail}) ?\n\n`
      + suivisWarning
      + `${account.health_rows} relevé(s) de santé (cycle, mental, sport) seront `
      + `détruits, ainsi que le profil et les données personnelles. `
      + `Seuls subsistent une coquille anonyme et le journal d'audit, que la notice `
      + `RGPD impose de conserver. Rien ne pourra être restauré.`,
      () => apiPurgeAccount(account.id, password, accessToken),
    );

  const handleDelete = () =>
    runErasure(
      `SUPPRIMER DÉFINITIVEMENT le compte de ${account.prenom} ${account.nom} `
      + `(${account.mail}) ?\n\n`
      + suivisWarning
      + `La ligne et toutes ses données seront effacées de la base. `
      + `Rien ne pourra être restauré, et il ne restera aucune trace du compte `
      + `en dehors de cette action dans le journal.`,
      () => apiDeleteAccount(account.id, password, accessToken),
    );

  return (
    <section className="admin-section">
      <h2 className="admin-section__title">Effacement d'un compte</h2>
      <p className="admin-page__notice--muted">
        Traitement des demandes d'effacement (RGPD Art.&nbsp;17). Recherche ciblée par
        email — aucune liste de comptes n'est affichée.
      </p>
      <p className="admin-page__notice--muted">
        <strong>Effacer toutes les données</strong> — <strong>geste par défaut</strong> pour
        toute demande de suppression : identité, profil et relevés de santé (cycle,
        mental, sport) sont détruits. Ne subsistent qu'une coquille anonyme et le journal
        d'audit, que la notice RGPD impose de conserver. C'est aussi ce que fait la
        suppression demandée par l'utilisatrice elle-même.
      </p>
      <p className="admin-page__notice--muted">
        <strong>Anonymiser</strong> efface les données identifiantes et libère l'adresse,
        mais <em>conserve</em> les relevés de santé, devenus non rattachables. À réserver
        aux cas où ces relevés doivent explicitement être gardés.
      </p>
      <p className="admin-page__notice--muted">
        <strong>Supprimer définitivement</strong> retire réellement la ligne de la base, et
        n'est possible que pour un compte sans aucune entrée d'audit — en pratique une
        inscription jamais activée. Dès qu'un compte a servi, la traçabilité prime.
      </p>

      <form className="admin-lookup" onSubmit={handleSearch}>
        <input
          type="email"
          className="admin-lookup__input"
          placeholder="email@exemple.com"
          value={email}
          required
          onChange={(e) => setEmail(e.target.value)}
        />
        <button className="admin-lookup__button" type="submit">
          Rechercher
        </button>
      </form>

      {error && <p className="admin-page__error">{error}</p>}
      {message && <p className="admin-page__notice--muted">{message}</p>}

      {account && (
        <div className="admin-card admin-lookup__result">
          <p>
            <strong>{account.prenom} {account.nom}</strong> — {account.mail}
          </p>
          <p className="admin-card__label">
            {ACCOUNT_TYPE_LABELS[account.type_client] || account.type_client}
            {account.statut ? ` (${account.statut})` : ''}
            {' · '}
            Inscrit(e) le{' '}
            {account.consent_at ? new Date(account.consent_at).toLocaleDateString('fr-FR') : '—'}
            {account.email_verified ? '' : ' · email non vérifié'}
          </p>
          <p className="admin-card__label">
            {account.suivis_count > 0
              ? `${account.suivis_count} suivi(s) coach↔sportive seront supprimés.`
              : 'Aucun suivi coach↔sportive rattaché.'}
            {' · '}
            {account.health_rows} relevé(s) de santé
            {' · '}
            {account.audit_entries} entrée(s) d'audit
          </p>

          {/* Le serveur revérifie ce mot de passe : porter un jeton d'accès valide
              ne suffit pas à effacer les données d'un tiers. Aucun geste n'est
              rattaché à la touche Entrée : chacun est irréversible et passe par un
              bouton explicite. L'effacement complet vient en premier, c'est le geste
              par défaut d'une demande de suppression. */}
          <form className="admin-lookup" onSubmit={(e) => e.preventDefault()}>
            <input
              type="password"
              className="admin-lookup__input"
              autoComplete="current-password"
              placeholder="Votre mot de passe admin"
              value={password}
              required
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="admin-lookup__button admin-lookup__button--danger"
              disabled={!password}
              onClick={handlePurge}
            >
              Effacer toutes les données
            </button>
            <button
              type="button"
              className="admin-lookup__button"
              disabled={!password}
              onClick={handleAnonymize}
            >
              Anonymiser
            </button>
            <button
              type="button"
              className="admin-lookup__button admin-lookup__button--danger"
              disabled={!account.can_hard_delete || !password}
              onClick={handleDelete}
            >
              Supprimer définitivement
            </button>
          </form>

          {!account.can_hard_delete && (
            <p className="admin-card__label">
              Suppression définitive indisponible : ce compte a un journal d'audit, qui
              doit être conservé. Il redeviendra supprimable une fois ce journal purgé par
              la rétention (13&nbsp;mois). D'ici là, «&nbsp;Effacer toutes les
              données&nbsp;» est l'effacement le plus complet possible.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

const TICKET_STATUS_FILTERS = [
  { value: '', label: 'Tous' },
  { value: 'ouvert', label: 'Ouverts' },
  { value: 'en_cours', label: 'En cours' },
  { value: 'resolu', label: 'Résolus' },
  { value: 'clos', label: 'Clos' },
];

const SupportTicketsSection = ({ accessToken }) => {
  const [statut, setStatut] = useState('');
  const [tickets, setTickets] = useState(null);
  // Un seul fil ouvert à la fois : le contenu n'est chargé qu'à l'ouverture, ce
  // qui évite de déchiffrer et de journaliser l'accès à des tickets non consultés.
  const [openTicket, setOpenTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    apiListTickets(accessToken, statut || undefined)
      .then(setTickets)
      .catch((err) => setError(err.message));
  }, [accessToken, statut]);

  useEffect(() => { load(); }, [load]);

  const run = async (action) => {
    setError('');
    try {
      setOpenTicket(await action());
      setReply('');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOpen = (id) => {
    if (openTicket && openTicket.id === id) {
      setOpenTicket(null);
      return;
    }
    setError('');
    apiGetTicket(id, accessToken).then(setOpenTicket).catch((err) => setError(err.message));
  };

  return (
    <section className="admin-section">
      <h2 className="admin-section__title">Tickets support</h2>
      <p className="admin-page__notice--muted">
        Les demandes de suppression de compte remontent en tête : elles courent contre le
        délai d'un mois de l'Art.&nbsp;12 RGPD. Chaque ouverture de fil est journalisée —
        un ticket peut contenir des données de santé. Répondre ici ne déclenche aucun
        e-mail : la personne lit la réponse dans l'application.
      </p>

      <div className="admin-lookup admin-lookup--wrap">
        {TICKET_STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className="admin-lookup__button"
            disabled={statut === f.value}
            onClick={() => { setStatut(f.value); setOpenTicket(null); }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <p className="admin-page__error">{error}</p>}

      {tickets && tickets.length === 0 && (
        <p className="admin-distribution__empty">Aucun ticket.</p>
      )}

      {tickets && tickets.map((t) => (
        <div key={t.id} className="admin-card admin-lookup__result">
          <p>
            <strong>{t.sujet}</strong>
            {t.categorie === 'suppression_compte' && ' — suppression de compte'}
          </p>
          <p className="admin-card__label">
            {t.auteur} (#{t.auteur_id}) · {TICKET_CATEGORY_LABELS[t.categorie] || t.categorie}
            {' · '}{TICKET_STATUS_LABELS[t.statut] || t.statut}
            {' · '}{t.messages_count} message(s)
            {' · maj '}{formatTicketDate(t.updated_at)}
          </p>

          <div className="admin-lookup__actions">
            <button className="admin-lookup__button" onClick={() => handleOpen(t.id)}>
              {openTicket && openTicket.id === t.id ? 'Fermer' : 'Ouvrir le fil'}
            </button>
            {['en_cours', 'resolu', 'clos'].includes(t.statut) || (
              <button
                className="admin-lookup__button"
                onClick={() => run(() => apiUpdateTicketStatus(t.id, 'en_cours', accessToken))}
              >
                Prendre en charge
              </button>
            )}
            <button
              className="admin-lookup__button"
              disabled={t.statut === 'resolu'}
              onClick={() => run(() => apiUpdateTicketStatus(t.id, 'resolu', accessToken))}
            >
              Marquer résolu
            </button>
            <button
              className="admin-lookup__button admin-lookup__button--danger"
              disabled={t.statut === 'clos'}
              onClick={() => run(() => apiUpdateTicketStatus(t.id, 'clos', accessToken))}
            >
              Clore
            </button>
          </div>

          {openTicket && openTicket.id === t.id && (
            <>
              <div className="admin-thread">
                {openTicket.messages.map((m) => (
                  <div
                    key={m.id}
                    className={`admin-thread__msg admin-thread__msg--${m.from_admin ? 'admin' : 'user'}`}
                  >
                    <p className="admin-thread__author">
                      {m.from_admin ? 'Administration' : openTicket.auteur}
                      {!m.auteur_actif && ' (compte supprimé)'}
                      {' · '}{formatTicketDate(m.created_at)}
                    </p>
                    <p className="admin-thread__body">{m.message}</p>
                  </div>
                ))}
              </div>

              {openTicket.statut === 'clos' ? (
                <p className="admin-card__label">
                  Ticket clos : plus aucun message ne peut y être ajouté.
                </p>
              ) : (
                <form
                  className="admin-lookup"
                  onSubmit={(e) => {
                    e.preventDefault();
                    run(() => apiReplyToTicket(t.id, reply.trim(), accessToken));
                  }}
                >
                  <input
                    type="text"
                    className="admin-lookup__input"
                    placeholder="Votre réponse"
                    value={reply}
                    required
                    onChange={(e) => setReply(e.target.value)}
                  />
                  <button className="admin-lookup__button" type="submit">Répondre</button>
                </form>
              )}
            </>
          )}
        </div>
      ))}
    </section>
  );
};

const CoachRequestCard = ({ coach, children }) => (
  <div className="admin-card admin-lookup__result">
    <p>
      <strong>{coach.prenom} {coach.nom}</strong> — {coach.mail}
    </p>
    <p className="admin-card__label">
      {coach.club ? `Club : ${coach.club} · ` : ''}
      Demande du {coach.consent_at ? new Date(coach.consent_at).toLocaleDateString('fr-FR') : '—'}
    </p>
    <div className="admin-lookup__actions">{children}</div>
  </div>
);

const CoachRequestsSection = ({ accessToken }) => {
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(() => {
    apiGetCoachRequests(accessToken)
      .then(setRequests)
      .catch((err) => setError(err.message));
  }, [accessToken]);

  useEffect(() => { load(); }, [load]);

  // Le backend renvoie les demandes en attente *et* refusées : un refus reste
  // réversible tant que la ligne n'a pas été anonymisée.
  const pending = requests ? requests.filter((c) => c.statut === 'pending') : [];
  const rejected = requests ? requests.filter((c) => c.statut === 'rejected') : [];

  const run = async (action, successMessage) => {
    setError('');
    setMessage('');
    try {
      const res = await action();
      setMessage((res && res.message) || successMessage);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleApprove = (id) =>
    run(() => apiApproveCoachRequest(id, accessToken), 'Demande validée.');

  const handleReject = (id) =>
    run(() => apiRejectCoachRequest(id, accessToken), 'Demande refusée.');

  const handleAnonymize = (coach) => {
    if (!window.confirm(
      `Anonymiser définitivement la demande de ${coach.prenom} ${coach.nom} ? `
      + `Ses données personnelles seront effacées et l'adresse ${coach.mail} redeviendra `
      + `disponible pour une nouvelle inscription. Cette action est irréversible.`
    )) return;
    return run(() => apiAnonymizeCoachRequest(coach.id, accessToken), 'Demande anonymisée.');
  };

  return (
    <section className="admin-section">
      <h2 className="admin-section__title">Demandes de coach</h2>
      <p className="admin-page__notice--muted">
        « Valider » envoie au coach un code de vérification par email ; « Refuser » clôt la
        demande sans notification, mais reste réversible. « Anonymiser » efface les données
        personnelles et libère l'adresse email pour une éventuelle nouvelle demande.
      </p>

      {error && <p className="admin-page__error">{error}</p>}
      {message && <p className="admin-page__notice--muted">{message}</p>}

      <h3 className="admin-distribution__title">En attente</h3>
      {requests && pending.length === 0 && (
        <p className="admin-distribution__empty">Aucune demande en attente.</p>
      )}
      {pending.map((c) => (
        <CoachRequestCard key={c.id} coach={c}>
          <button className="admin-lookup__button" onClick={() => handleApprove(c.id)}>
            Valider et envoyer le code
          </button>
          <button className="admin-lookup__button" onClick={() => handleReject(c.id)}>
            Refuser
          </button>
        </CoachRequestCard>
      ))}

      <h3 className="admin-distribution__title">Refusées</h3>
      {requests && rejected.length === 0 && (
        <p className="admin-distribution__empty">Aucune demande refusée.</p>
      )}
      {rejected.map((c) => (
        <CoachRequestCard key={c.id} coach={c}>
          <button className="admin-lookup__button" onClick={() => handleApprove(c.id)}>
            Finalement valider
          </button>
          <button
            className="admin-lookup__button admin-lookup__button--danger"
            onClick={() => handleAnonymize(c)}
          >
            Anonymiser
          </button>
        </CoachRequestCard>
      ))}
    </section>
  );
};

const GuardianConsentSection = ({ accessToken }) => {
  const [pending, setPending] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(() => {
    apiGetGuardianPending(accessToken)
      .then(setPending)
      .catch((err) => setError(err.message));
  }, [accessToken]);

  useEffect(() => { load(); }, [load]);

  const handleRecord = async (sportive) => {
    // Confirmation explicite : l'admin atteste avoir vu le document papier, et
    // son identité est enregistrée avec l'autorisation. Ce n'est pas une case
    // qu'on coche à la légère.
    if (!window.confirm(
      `Confirmer avoir reçu l'autorisation signée du représentant légal de `
      + `${sportive.prenom} ${sportive.nom} (${sportive.age} ans) ?\n\n`
      + `Votre identité sera enregistrée avec cette attestation, et le compte `
      + `pourra se connecter immédiatement.`
    )) return;

    setError('');
    setMessage('');
    try {
      const res = await apiRecordGuardianConsent(sportive.id, accessToken);
      setMessage(res.message);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <section className="admin-section">
      <h2 className="admin-section__title">Autorisations parentales</h2>
      <p className="admin-page__notice--muted">
        Sportives mineures dont le compte est bloqué tant que l'autorisation signée du
        représentant légal, recueillie par le club, n'a pas été enregistrée. Le formulaire
        papier doit être archivé par GEHB : c'est la seule pièce produisible en cas de contrôle.
      </p>

      {error && <p className="admin-page__error">{error}</p>}
      {message && <p className="admin-page__notice--muted">{message}</p>}

      {pending && pending.length === 0 && (
        <p className="admin-distribution__empty">Aucune autorisation en attente.</p>
      )}
      {(pending || []).map((s) => (
        <div key={s.id} className="admin-card admin-lookup__result">
          <p>
            <strong>{s.prenom} {s.nom}</strong> — {s.mail}
          </p>
          <p className="admin-card__label">
            {s.age} ans · Inscription du{' '}
            {s.consent_at ? new Date(s.consent_at).toLocaleDateString('fr-FR') : '—'}
          </p>
          <div className="admin-lookup__actions">
            <button className="admin-lookup__button" onClick={() => handleRecord(s)}>
              Autorisation reçue
            </button>
          </div>
        </div>
      ))}
    </section>
  );
};

const SURVEY_SCOPES = [
  { value: 'read', label: 'Consulter / exporter les réponses' },
  { value: 'phase', label: 'Piloter la phase de l\'étude' },
  { value: 'participants', label: 'Tenir le registre des codes participants' },
];

const SURVEY_ACCESS_STATUS = {
  en_attente: 'En attente d\'activation',
  actif: 'Actif',
  revoque: 'Révoqué',
  expire: 'Expiré',
  activation_expiree: 'Lien d\'activation expiré',
};

const SurveyAccessSection = ({ accessToken }) => {
  const [accesses, setAccesses] = useState(null);
  const [form, setForm] = useState({ label: '', email: '', scopes: ['read'], ttl_days: 180 });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(() => {
    apiGetSurveyAccesses(accessToken)
      .then(setAccesses)
      .catch((err) => setError(err.message));
  }, [accessToken]);

  useEffect(() => { load(); }, [load]);

  const toggleScope = (scope) => setForm((f) => ({
    ...f,
    scopes: f.scopes.includes(scope)
      ? f.scopes.filter((s) => s !== scope)
      : [...f.scopes, scope],
  }));

  const handleIssue = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!form.scopes.length) { setError('Sélectionne au moins un droit.'); return; }
    try {
      const res = await apiIssueSurveyAccess(form, accessToken);
      setMessage(`Lien d'activation envoyé à ${res.activation_sent_to}. Il expire dans 24 h.`);
      setForm({ label: '', email: '', scopes: ['read'], ttl_days: 180 });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRevoke = async (access) => {
    if (!window.confirm(`Révoquer l'accès de ${access.label} ? Il sera coupé immédiatement.`)) return;
    setError('');
    setMessage('');
    try {
      await apiRevokeSurveyAccess(access.id, accessToken);
      setMessage(`Accès de ${access.label} révoqué.`);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (access) => {
    if (!window.confirm(
      `Supprimer définitivement l'accès de ${access.label} (${access.email}) ?\n\n`
      + `La ligne disparaît, nom et adresse compris : il ne restera aucune trace de `
      + `qui a détenu cet accès ni de ses usages. Préférez « Révoquer » si vous `
      + `voulez seulement couper l'accès.\n\n`
      + `Les réponses aux questionnaires ne sont pas concernées.`,
    )) return;
    setError('');
    setMessage('');
    try {
      await apiDeleteSurveyAccess(access.id, accessToken);
      setMessage(`Accès de ${access.label} supprimé.`);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <section className="admin-section">
      <h2 className="admin-section__title">Accès aux questionnaires</h2>
      <p className="admin-page__notice--muted">
        Accès nominatifs au module d'étude. Chaque usage est horodaté au nom du porteur
        et l'accès est révocable individuellement. Le jeton n'est jamais envoyé par
        e-mail : le destinataire reçoit un lien d'activation à usage unique, valable
        24 h, qu'il échange lui-même contre son jeton.
      </p>

      {error && <p className="admin-page__error">{error}</p>}
      {message && <p className="admin-page__notice--muted">{message}</p>}

      <form className="admin-card" onSubmit={handleIssue}>
        <input
          className="admin-lookup__input" type="text" placeholder="Nom du porteur"
          value={form.label} required maxLength={120}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
        <input
          className="admin-lookup__input" type="email" placeholder="Adresse e-mail"
          value={form.email} required
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <div className="admin-lookup__actions">
          {SURVEY_SCOPES.map((s) => (
            <label key={s.value} className="admin-card__label">
              <input
                type="checkbox"
                checked={form.scopes.includes(s.value)}
                onChange={() => toggleScope(s.value)}
              />{' '}
              {s.label}
            </label>
          ))}
        </div>
        <label className="admin-card__label">
          Validité (jours) :{' '}
          <input
            className="admin-lookup__input" type="number" min={1} max={730}
            value={form.ttl_days}
            onChange={(e) => setForm({ ...form, ttl_days: Number(e.target.value) })}
          />
        </label>
        <div className="admin-lookup__actions">
          <button className="admin-lookup__button" type="submit">
            Ouvrir l'accès et envoyer le lien
          </button>
        </div>
      </form>

      {accesses && accesses.length === 0 && (
        <p className="admin-distribution__empty">Aucun accès ouvert.</p>
      )}

      {accesses && accesses.map((a) => (
        <div key={a.id} className="admin-card admin-lookup__result">
          <p><strong>{a.label}</strong> — {a.email}</p>
          <p className="admin-card__label">
            {SURVEY_ACCESS_STATUS[a.status] || a.status}
            {' · droits : '}{a.scopes.join(', ')}
            {' · '}{a.use_count} utilisation{a.use_count > 1 ? 's' : ''}
            {a.last_used_at
              ? ` · dernier usage le ${new Date(a.last_used_at).toLocaleString('fr-FR')}`
              : ' · jamais utilisé'}
          </p>
          {/* Révoquer coupe l'accès en gardant la trace ; supprimer retire aussi le
              nom et l'adresse du porteur. Les deux restent proposés sur un accès déjà
              révoqué : c'est justement là que la demande d'effacement arrive. */}
          <div className="admin-lookup__actions">
            {a.status !== 'revoque' && (
              <button className="admin-lookup__button" onClick={() => handleRevoke(a)}>
                Révoquer
              </button>
            )}
            <button
              className="admin-lookup__button admin-lookup__button--danger"
              onClick={() => handleDelete(a)}
            >
              Supprimer
            </button>
          </div>
        </div>
      ))}
    </section>
  );
};

const EditorsSection = ({ accessToken }) => {
  const [editors, setEditors] = useState(null);
  const [form, setForm] = useState({ mail: '', prenom: '', nom: '' });
  const [grantMail, setGrantMail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(() => {
    apiGetEditors(accessToken)
      .then(setEditors)
      .catch((err) => setError(err.message));
  }, [accessToken]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      const res = await apiCreateEditor(
        { mail: form.mail.trim(), prenom: form.prenom.trim(), nom: form.nom.trim() },
        accessToken,
      );
      setMessage(
        `Compte créé. Un code de définition de mot de passe a été envoyé à `
        + `${res.password_setup_sent_to} ; il expire dans 30 min. Passé ce délai, `
        + `l'éditeur peut en redemander un via « Mot de passe oublié ».`,
      );
      setForm({ mail: '', prenom: '', nom: '' });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleGrant = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      const granted = await apiGrantBlogAccess(grantMail.trim(), accessToken);
      setMessage(
        `${granted.prenom} ${granted.nom} peut désormais écrire sur le blog. `
        + `Son compte et ses données sont inchangés ; l'espace rédaction lui est `
        + `proposé depuis la page Blog.`,
      );
      setGrantMail('');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRevoke = async (ed) => {
    if (!window.confirm(
      `Retirer l'accès à la rédaction du blog pour ${ed.prenom} ${ed.nom} (${ed.mail}) ?\n\n`
      + `Le compte et ses données ne sont pas touchés — seule la capacité d'écrire `
      + `des articles est retirée.`,
    )) return;
    setError('');
    setMessage('');
    try {
      await apiRevokeBlogAccess(ed.id, accessToken);
      setMessage(`Accès retiré pour ${ed.mail}.`);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <section className="admin-section">
      <h2 className="admin-section__title">Accès à la rédaction du blog</h2>
      <p className="admin-page__notice--muted">
        Écrire sur le blog ne donne accès ni aux données de santé ni à ce dashboard.
        Deux façons d'ouvrir cet accès : <strong>créer un compte de rédaction dédié</strong>,
        pour quelqu'un qui n'utilise pas l'application ; ou <strong>ouvrir le blog à un
        compte existant</strong> — une sportive ou un coach garde alors son rôle, ses
        données et son écran d'accueil, et gagne seulement l'espace rédaction.
      </p>

      {error && <p className="admin-page__error">{error}</p>}
      {message && <p className="admin-page__notice--muted">{message}</p>}

      <form className="admin-card" onSubmit={handleGrant}>
        <p className="admin-card__label">Ouvrir le blog à un compte existant</p>
        <input
          className="admin-lookup__input" type="email" placeholder="Adresse e-mail du compte"
          value={grantMail} required
          onChange={(e) => setGrantMail(e.target.value)}
        />
        <div className="admin-lookup__actions">
          <button className="admin-lookup__button" type="submit">
            Accorder l'accès
          </button>
        </div>
      </form>

      <form className="admin-card" onSubmit={handleCreate}>
        <p className="admin-card__label">
          Créer un compte de rédaction dédié
        </p>
        <p className="admin-page__notice--muted">
          Le mot de passe n'est pas choisi ici : le compte naît sans mot de passe
          utilisable et son titulaire définit le sien avec le code reçu par e-mail.
        </p>
        <input
          className="admin-lookup__input" type="email" placeholder="Adresse e-mail"
          value={form.mail} required
          onChange={(e) => setForm({ ...form, mail: e.target.value })}
        />
        <input
          className="admin-lookup__input" type="text" placeholder="Prénom"
          value={form.prenom} required maxLength={50}
          onChange={(e) => setForm({ ...form, prenom: e.target.value })}
        />
        <input
          className="admin-lookup__input" type="text" placeholder="Nom"
          value={form.nom} required maxLength={50}
          onChange={(e) => setForm({ ...form, nom: e.target.value })}
        />
        <div className="admin-lookup__actions">
          <button className="admin-lookup__button" type="submit">
            Créer le compte et envoyer le code
          </button>
        </div>
      </form>

      {editors && editors.length === 0 && (
        <p className="admin-distribution__empty">Aucun accès à la rédaction.</p>
      )}

      {editors && editors.map((ed) => {
        // Un compte `editor` n'existe que pour le blog : lui retirer la capacité ne
        // ferait rien, son type la lui rendrait. C'est l'effacement de compte qui le
        // retire — d'où le renvoi plutôt qu'un bouton sans effet.
        const isDedicated = ed.type_client === 'editor';
        return (
          <div key={ed.id} className="admin-card admin-lookup__result">
            <p><strong>{ed.prenom} {ed.nom}</strong> — {ed.mail}</p>
            <p className="admin-card__label">
              {isDedicated
                ? 'Compte de rédaction dédié'
                : `${ACCOUNT_TYPE_LABELS[ed.type_client] || ed.type_client} avec accès au blog`}
              {' · '}
              Créé le{' '}
              {ed.consent_at ? new Date(ed.consent_at).toLocaleDateString('fr-FR') : '—'}
            </p>
            {isDedicated ? (
              <p className="admin-card__label">
                Pour retirer cet accès, effacez le compte depuis « Effacement d'un compte ».
              </p>
            ) : (
              <div className="admin-lookup__actions">
                <button
                  type="button"
                  className="admin-lookup__button admin-lookup__button--danger"
                  onClick={() => handleRevoke(ed)}
                >
                  Retirer l'accès
                </button>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
};

const EMPTY_GOV_MEMBER_FORM = { mail: '', prenom: '', nom: '', password: '' };

const GovMembersSection = ({ accessToken }) => {
  const [membres, setMembres] = useState(null);
  const [form, setForm] = useState(EMPTY_GOV_MEMBER_FORM);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(() => {
    apiGetGovMembers(accessToken)
      .then(setMembres)
      .catch((err) => setError(err.message));
  }, [accessToken]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      const membre = await apiCreateGovMember(
        {
          mail: form.mail.trim(), prenom: form.prenom.trim(), nom: form.nom.trim(),
          password: form.password,
        },
        accessToken,
      );
      setMessage(
        `Membre créé. Un code valable 24 h a été envoyé à ${membre.mail} pour choisir `
        + `son mot de passe sur /gouvernance/mot-de-passe.`,
      );
      setForm(EMPTY_GOV_MEMBER_FORM);
      load();
    } catch (err) {
      setError(err.message);
      setForm((f) => ({ ...f, password: '' }));
    }
  };

  return (
    <section className="admin-section">
      <h2 className="admin-section__title">Membres de la gouvernance</h2>
      <p className="admin-page__notice--muted">
        Le dashboard de gouvernance a ses propres comptes : ce compte admin n'y donne
        pas accès. Le mot de passe du membre n'est pas choisi ici — il le définit
        lui-même avec le code reçu par e-mail. Désactiver un membre se fait depuis le
        dashboard de gouvernance.
      </p>

      {error && <p className="admin-page__error">{error}</p>}
      {message && <p className="admin-page__notice--muted">{message}</p>}

      <form className="admin-card" onSubmit={handleCreate}>
        <p className="admin-card__label">Créer un membre</p>
        <input
          className="admin-lookup__input" type="email" placeholder="Adresse e-mail"
          value={form.mail} required
          onChange={(e) => setForm({ ...form, mail: e.target.value })}
        />
        <input
          className="admin-lookup__input" type="text" placeholder="Prénom"
          value={form.prenom} required maxLength={50}
          onChange={(e) => setForm({ ...form, prenom: e.target.value })}
        />
        <input
          className="admin-lookup__input" type="text" placeholder="Nom"
          value={form.nom} required maxLength={50}
          onChange={(e) => setForm({ ...form, nom: e.target.value })}
        />
        <input
          className="admin-lookup__input" type="password" placeholder="Votre mot de passe admin"
          autoComplete="current-password" value={form.password} required maxLength={128}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
        <div className="admin-lookup__actions">
          <button className="admin-lookup__button" type="submit">
            Créer le membre et envoyer le code
          </button>
        </div>
      </form>

      {membres && membres.length === 0 && (
        <p className="admin-distribution__empty">Aucun membre.</p>
      )}

      {membres && membres.map((m) => (
        <div key={m.id} className="admin-card admin-lookup__result">
          <p><strong>{m.prenom} {m.nom}</strong> — {m.mail}</p>
          <p className="admin-card__label">
            {m.actif ? 'Actif' : 'Désactivé'}
            {' · '}
            {m.last_login_at
              ? `Dernière connexion le ${new Date(m.last_login_at).toLocaleDateString('fr-FR')}`
              : 'Jamais connecté'}
          </p>
        </div>
      ))}
    </section>
  );
};

const EMPTY_CREDENTIALS_FORM = {
  current_password: '',
  admin_token: '',
  new_password: '',
  confirm_password: '',
  rotate_token: false,
};

const CredentialsSection = ({ accessToken }) => {
  const [form, setForm] = useState(EMPTY_CREDENTIALS_FORM);
  const [issuedToken, setIssuedToken] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    // Le serveur ne peut pas détecter une faute de frappe : sans confirmation
    // locale, elle enfermerait dehors le seul compte capable de se corriger.
    if (form.new_password !== form.confirm_password) {
      setError('Les deux saisies du nouveau mot de passe diffèrent.');
      return;
    }
    try {
      const res = await apiChangeAdminPassword({
        current_password: form.current_password,
        admin_token: form.admin_token,
        new_password: form.new_password,
        rotate_token: form.rotate_token,
      }, accessToken);
      setForm(EMPTY_CREDENTIALS_FORM);
      setMessage(res.message);
      if (res.admin_token) setIssuedToken(res.admin_token);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <section className="admin-section">
      <h2 className="admin-section__title">Mes identifiants</h2>
      <p className="admin-page__notice--muted">
        Le compte admin est exclu du flux «&nbsp;mot de passe oublié&nbsp;» : c'est ici,
        et nulle part ailleurs, que son mot de passe se change. Le mot de passe actuel
        et le jeton secret sont redemandés — être connecté ne suffit pas. Le compte
        n'est pas recréé : identifiant, adresse e-mail et journal d'audit sont conservés.
        Toutes les sessions sont révoquées ensuite, il faudra se reconnecter.
      </p>

      {error && <p className="admin-page__error">{error}</p>}
      {message && <p className="admin-page__notice--muted">{message}</p>}

      {issuedToken && (
        <div className="admin-card admin-lookup__result">
          <p><strong>Nouveau jeton secret</strong> — copie-le maintenant.</p>
          <p className="admin-card__label">
            Il n'est affiché qu'une fois : seule son empreinte est conservée en base,
            aucune page ne pourra le réafficher. Sans lui, plus de connexion possible.
          </p>
          <code className="admin-token">{issuedToken}</code>
        </div>
      )}

      <form className="admin-card" onSubmit={handleSubmit}>
        <input
          className="admin-lookup__input" type="password" autoComplete="current-password"
          placeholder="Mot de passe actuel" value={form.current_password} required
          onChange={(e) => setForm({ ...form, current_password: e.target.value })}
        />
        <input
          className="admin-lookup__input" type="password" autoComplete="off"
          placeholder="Jeton secret actuel" value={form.admin_token} required
          onChange={(e) => setForm({ ...form, admin_token: e.target.value })}
        />
        <input
          className="admin-lookup__input" type="password" autoComplete="new-password"
          placeholder="Nouveau mot de passe (12 car., majuscule, chiffre, spécial)"
          value={form.new_password} required minLength={12}
          onChange={(e) => setForm({ ...form, new_password: e.target.value })}
        />
        <input
          className="admin-lookup__input" type="password" autoComplete="new-password"
          placeholder="Confirmer le nouveau mot de passe"
          value={form.confirm_password} required minLength={12}
          onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
        />
        <label className="admin-card__label">
          <input
            type="checkbox" checked={form.rotate_token}
            onChange={(e) => setForm({ ...form, rotate_token: e.target.checked })}
          />{' '}
          Régénérer aussi le jeton secret (à faire si quelqu'un d'autre l'a vu)
        </label>
        <div className="admin-lookup__actions">
          <button className="admin-lookup__button" type="submit">
            Mettre à jour mes identifiants
          </button>
        </div>
      </form>
    </section>
  );
};

const AUDIT_LOG_PAGE_SIZE = 25;

const AuditLogViewer = ({ accessToken }) => {
  const [logs, setLogs] = useState(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ action: '', user_id: '', date_from: '', date_to: '' });
  const [appliedFilters, setAppliedFilters] = useState({});

  useEffect(() => {
    apiGetAuditLogs(accessToken, { ...appliedFilters, page, page_size: AUDIT_LOG_PAGE_SIZE })
      .then(setLogs)
      .catch((err) => setError(err.message));
  }, [accessToken, appliedFilters, page]);

  const handleFilterChange = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleApplyFilters = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const totalPages = logs ? Math.max(1, Math.ceil(logs.total / logs.page_size)) : 1;

  return (
    <section className="admin-section">
      <h2 className="admin-section__title">Journal d'accès</h2>
      <p className="admin-page__notice--muted">
        Historique des actions journalisées (connexions, consultations, exports). Aucune donnée
        médicale n'est affichée ici — uniquement l'action, l'horodatage et l'utilisateur concerné.
      </p>
      <div className="admin-lookup admin-lookup--wrap">
        <input
          type="text"
          className="admin-lookup__input"
          placeholder="Action (ex: LOGIN_SUCCESS)"
          value={filters.action}
          onChange={handleFilterChange('action')}
        />
        <input
          type="number"
          className="admin-lookup__input"
          placeholder="ID utilisateur"
          value={filters.user_id}
          onChange={handleFilterChange('user_id')}
        />
        <input
          type="datetime-local"
          className="admin-lookup__input"
          value={filters.date_from}
          onChange={handleFilterChange('date_from')}
        />
        <input
          type="datetime-local"
          className="admin-lookup__input"
          value={filters.date_to}
          onChange={handleFilterChange('date_to')}
        />
        <button className="admin-lookup__button" onClick={handleApplyFilters}>
          Filtrer
        </button>
      </div>

      {error && <p className="admin-page__error">{error}</p>}

      {logs && (
        <>
          <div className="admin-audit-table-wrap">
            <table className="admin-audit-table">
              <thead>
                <tr>
                  <th>Horodatage</th>
                  <th>Action</th>
                  <th>Utilisateur</th>
                  <th>Ressource</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="admin-distribution__empty">Aucun événement.</td>
                  </tr>
                ) : (
                  logs.items.map((entry) => (
                    <tr key={entry.id}>
                      <td>{new Date(entry.created_at).toLocaleString('fr-FR')}</td>
                      <td>{entry.action}</td>
                      <td>{entry.user_id ?? '—'}</td>
                      <td>
                        {entry.resource_type
                          ? `${entry.resource_type}${entry.resource_id ? ` #${entry.resource_id}` : ''}`
                          : '—'}
                      </td>
                      <td>{entry.ip_address ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="admin-audit-pagination">
            <button
              className="admin-lookup__button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              ‹ Précédent
            </button>
            <span className="admin-page__notice--muted">
              Page {logs.page} / {totalPages} — {logs.total} événement(s)
            </span>
            <button
              className="admin-lookup__button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Suivant ›
            </button>
          </div>
        </>
      )}
    </section>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const { accessToken, logoutUser } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    apiGetAdminDashboard(accessToken)
      .then(setData)
      .catch((err) => setError(err.message));
  }, [accessToken]);

  const handleLogout = async () => {
    await logoutUser();
    navigate('/admin/connexion');
  };

  if (error) {
    return (
      <div className="admin-page">
        <div className="admin-page__inner">
          <p className="admin-page__error">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="admin-page">
        <div className="admin-page__inner">
          <p className="admin-page__loading">Chargement…</p>
        </div>
      </div>
    );
  }

  const { operational: op, aggregated: agg } = data;

  return (
    <div className="admin-page">
      <div className="admin-page__inner">
        <div className="admin-page__header">
          <h1 className="admin-page__title">Dashboard CTO</h1>
          <button className="admin-page__logout" onClick={handleLogout}>
            Déconnexion
          </button>
        </div>

        <SupportTicketsSection accessToken={accessToken} />

        <CoachRequestsSection accessToken={accessToken} />

        <GuardianConsentSection accessToken={accessToken} />

        <UnverifiedAccountLookup accessToken={accessToken} />

        <AccountErasureSection accessToken={accessToken} />

        {/* ── Opérationnel ────────────────────────────────────────────────── */}
        <section className="admin-section">
          <h2 className="admin-section__title">Opérationnel</h2>
          <div className="admin-grid">
            <StatCard label="Sportives" value={op.total_sportives} color="blue" />
            <StatCard label="Coachs" value={op.total_coaches} color="blue" />
            <StatCard label="Sportives actives" value={op.active_sportives} color="blue" />
            <StatCard label="Comptes verrouillés" value={op.locked_accounts} color="red" />
            <StatCard label="Inscriptions (7j)" value={op.signups_last_7d} color="orange" />
            <StatCard label="Inscriptions (30j)" value={op.signups_last_30d} color="orange" />
            <StatCard label="Consentement à jour" value={`${op.consent_current_pct}%`} color="yellow" />
            <StatCard label="Logins réussis (30j)" value={op.login_success_30d} color="blue" />
            <StatCard label="Logins échoués (30j)" value={op.login_failed_30d} color="red" />
            <StatCard label="OTP échoués (30j)" value={op.otp_failed_30d} color="red" />
            <StatCard label="Comptes effacés (30j)" value={op.accounts_anonymized_30d} color="yellow" />
            <StatCard label="Exports de données (30j)" value={op.data_exports_30d} color="orange" />
          </div>
        </section>

        {/* ── Agrégé k-anonymisé ─────────────────────────────────────────── */}
        <section className="admin-section">
          <h2 className="admin-section__title">Agrégé (k-anonymisé)</h2>
          {agg.insufficient_data ? (
            <p className="admin-page__notice">
              Échantillon insuffisant ({agg.sample_size} sportive(s) — seuil requis&nbsp;:&nbsp;
              {agg.k_anonymity_threshold}). Aucune statistique agrégée disponible.
            </p>
          ) : (
            <>
              <p className="admin-page__notice--muted">
                Basé sur {agg.sample_size} sportives. Tout groupe sous {agg.k_anonymity_threshold} individus
                est fusionné dans «&nbsp;Autre (groupé)&nbsp;».
              </p>
              <div className="admin-grid">
                <StatCard label="Température basale moy." value={agg.avg_basal_temperature ?? '—'} color="blue" />
                <StatCard label="Concentration moy." value={agg.avg_concentration ?? '—'} color="blue" />
                <StatCard label="Productivité moy." value={agg.avg_productivity ?? '—'} color="blue" />
                <StatCard label="Estime de soi moy." value={agg.avg_self_esteem ?? '—'} color="blue" />
                <StatCard label="RPE moyen" value={agg.avg_rpe ?? '—'} color="orange" />
                <StatCard
                  label="Taux de blessure"
                  value={agg.injury_rate_pct != null ? `${agg.injury_rate_pct}%` : '—'}
                  color="yellow"
                />
              </div>
              <div className="admin-distributions">
                <DistributionList title="Contraception" buckets={agg.contraception_distribution} />
                <DistributionList title="Niveau sportif" buckets={agg.sport_level_distribution} />
                <DistributionList title="Flux menstruel" buckets={agg.flow_distribution} />
                <DistributionList title="Humeur" buckets={agg.mood_distribution} />
                <DistributionList title="Essoufflement" buckets={agg.breathlessness_distribution} />
              </div>
            </>
          )}
        </section>

        <SurveyAccessSection accessToken={accessToken} />

        <EditorsSection accessToken={accessToken} />

        <GovMembersSection accessToken={accessToken} />

        <CredentialsSection accessToken={accessToken} />

        <AuditLogViewer accessToken={accessToken} />
      </div>
    </div>
  );
};

export default Admin;
